import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Banner,
  Button,
  Card,
  IndexTable,
  Modal,
  Pagination,
  Select,
  TextField,
} from "@shopify/polaris";
import { Link, useNavigate } from "react-router-dom";
import {
  getSupportStoreDispute,
  getSupportStoreDisputes,
} from "../../../services/supportApi";
import {
  formatCount,
  formatDateTime,
  formatRelativeTime,
  readableStatus,
  statusTone,
} from "../../support/supportFormatters";
import { useStoreWorkspace } from "../../storeSupport/StoreWorkspaceContext";
import WorkspaceState from "../../storeSupport/WorkspaceState";
import { workspaceModel } from "../../storeSupport/workspaceModel";
import StorePageFrame from "./StorePageFrame";
import "../../storeSupport/style.css";

const valueOf = (source, ...keys) =>
  keys.map((key) => source?.[key]).find((value) => value !== null && value !== undefined);

export const normalizeSupportDispute = (source = {}) => {
  const amountValue =
    valueOf(source.amount, "value") ??
    valueOf(source.disputeAmount, "value") ??
    valueOf(source, "amountValue", "amount");
  const currency =
    valueOf(source.amount, "currencyCode", "currency_code") ||
    valueOf(source.disputeAmount, "currencyCode", "currency_code") ||
    valueOf(source, "currencyCode", "currency") ||
    null;
  return {
    ...source,
    id: String(valueOf(source, "disputeId", "dispute_id", "caseId", "id") || ""),
    order: valueOf(
      source,
      "orderName",
      "invoiceNumber",
      "orderId",
      "order_id",
      "storeOrderId"
    ),
    transaction: valueOf(
      source,
      "transactionId",
      "transaction_id",
      "paypalTransactionId",
      "sellerTransactionId"
    ),
    account: valueOf(
      source,
      "paypalAccountLabel",
      "paypalAccountEmail",
      "paypalAccountId"
    ),
    status: valueOf(source, "status", "disputeStatus", "state") || "UNKNOWN",
    stage: valueOf(source, "stage", "disputeStage", "lifecycleStage"),
    reason: valueOf(source, "reason", "reasonCode", "disputeReason"),
    outcome: valueOf(source, "outcomeCode", "outcome", "decision"),
    amountValue,
    currency,
    responseDueAt: valueOf(
      source,
      "sellerResponseDueAt",
      "responseDueAt",
      "responseDueDate",
      "response_due_date"
    ),
    updatedAt: valueOf(
      source,
      "paypalUpdatedAt",
      "updatedAt",
      "updateTime",
      "lastUpdatedAt",
      "modifiedAt"
    ),
    createdAt: valueOf(source, "paypalCreatedAt", "createdAt", "createTime", "openedAt"),
    recommendedAction: valueOf(
      source,
      "recommendedAction",
      "nextAction",
      "nextStep",
      "supportSummary"
    ),
    allowedActions: Array.isArray(source.allowedActions) ? source.allowedActions : [],
    activityCount: valueOf(source, "activityCount"),
    failedActivityCount: valueOf(source, "failedActivityCount"),
    isSample:
      source.isSample === true || source.isFixture === true || source.fixture === true,
  };
};

const formatMoney = (value, currency) => {
  if (value === null || value === undefined || value === "") return "Not recorded";
  const number = Number(value);
  if (!Number.isFinite(number)) return "Not recorded";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(number);
  } catch (_error) {
    return `${currency || ""} ${number.toFixed(2)}`.trim();
  }
};

const isOpen = (status) =>
  ![
    "RESOLVED",
    "CLOSED",
    "CANCELLED",
    "DENIED",
    "RESOLVED_BUYER_FAVOUR",
    "RESOLVED_SELLER_FAVOUR",
  ].includes(String(status || "").toUpperCase());

export const requiresSupportDisputeAttention = (dispute) => {
  const status = String(dispute.status || "").toUpperCase();
  if (!isOpen(status)) return false;
  if (["REQUIRED_ACTION", "ACTION_REQUIRED", "WAITING_FOR_SELLER_RESPONSE"].includes(status)) {
    return true;
  }
  return Boolean(dispute.responseDueAt) || dispute.allowedActions.length > 0;
};

export const disputeOrderSearchPath = (shopId, dispute = {}) => {
  const normalizedShopId = String(shopId ?? "").trim();
  const orderReference = String(dispute.order || "").trim();
  const reference = orderReference
    ? orderReference.replace(/^#+\s*/, "") || orderReference
    : String(dispute.transaction || "").trim();
  if (!normalizedShopId || !reference) return null;
  const search = new URLSearchParams({ query: reference });
  return `/store/${encodeURIComponent(normalizedShopId)}/orders?${search.toString()}`;
};

const requiresAttention = requiresSupportDisputeAttention;

const deadlineStatus = (dispute) => {
  if (!dispute.responseDueAt) return "No seller deadline";
  const due = new Date(dispute.responseDueAt).getTime();
  if (!Number.isFinite(due)) return "Deadline not recorded";
  if (due < Date.now()) return `Overdue · ${formatRelativeTime(dispute.responseDueAt)}`;
  return `Due ${formatRelativeTime(dispute.responseDueAt)}`;
};

const Metric = ({ label, value, hint, tone }) => (
  <Card>
    <div className={`store-support-dispute-metric store-support-dispute-metric--${tone || "neutral"}`}>
      <span>{label}</span>
      <strong>{formatCount(value)}</strong>
      <small>{hint}</small>
    </div>
  </Card>
);

const DetailDefinition = ({ label, value }) => (
  <div className="store-support-definition">
    <dt>{label}</dt>
    <dd>{value}</dd>
  </div>
);

const DisputeDetailModal = ({ shopId, disputeId, onClose }) => {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    if (!disputeId) return undefined;
    const controller = new AbortController();
    setState({ loading: true, data: null, error: null });
    getSupportStoreDispute(shopId, disputeId, { signal: controller.signal })
      .then((data) => setState({ loading: false, data, error: null }))
      .catch((error) => {
        if (error?.name !== "AbortError") {
          setState({ loading: false, data: null, error: error?.message || "Case detail is unavailable." });
        }
      });
    return () => controller.abort();
  }, [disputeId, shopId]);

  const dispute = normalizeSupportDispute(state.data?.dispute || {});
  const orderSearchPath = disputeOrderSearchPath(shopId, dispute);
  const automation = state.data?.automation || {};
  const activities = Array.isArray(state.data?.activity) ? state.data.activity : [];

  return (
    <Modal
      open={Boolean(disputeId)}
      onClose={onClose}
      title={disputeId ? `Dispute ${disputeId}` : "Dispute detail"}
      primaryAction={{ content: "Close", onAction: onClose }}
      size="large"
    >
      {state.loading ? (
        <Modal.Section><p role="status">Loading dispute detail…</p></Modal.Section>
      ) : state.error ? (
        <Modal.Section>
          <Banner title="Dispute detail is unavailable" status="critical"><p>{state.error}</p></Banner>
        </Modal.Section>
      ) : (
        <>
          <Modal.Section>
            <div className="store-support-case-header">
              <div>
                <Badge status={statusTone(requiresAttention(dispute) ? "ATTENTION" : dispute.status)}>
                  {readableStatus(dispute.status)}
                </Badge>
                {dispute.stage ? <span>{readableStatus(dispute.stage)}</span> : null}
                {dispute.isSample ? <Badge status="info">Sample</Badge> : null}
              </div>
              <span>Observed {formatDateTime(state.data?.observedAt)}</span>
            </div>
            <dl className="store-support-definition-grid store-support-definition-grid--four">
              <DetailDefinition
                label="Matched order"
                value={
                  orderSearchPath ? (
                    <Link to={orderSearchPath}>
                      {dispute.order || "Find matching order"}
                    </Link>
                  ) : (
                    "Not matched"
                  )
                }
              />
              <DetailDefinition label="Seller transaction" value={dispute.transaction || "Not recorded"} />
              <DetailDefinition label="PayPal account" value={dispute.account || "Not recorded"} />
              <DetailDefinition label="Amount" value={formatMoney(dispute.amountValue, dispute.currency)} />
              <DetailDefinition label="Reason" value={readableStatus(dispute.reason, "Not recorded")} />
              <DetailDefinition label="Outcome" value={readableStatus(dispute.outcome, "Pending")} />
              <DetailDefinition label="Opened" value={formatDateTime(dispute.createdAt)} />
              <DetailDefinition label="Seller deadline" value={dispute.responseDueAt ? formatDateTime(dispute.responseDueAt) : "None returned"} />
            </dl>
            {dispute.allowedActions.length ? (
              <div className="store-support-case-actions">
                <strong>Actions currently allowed by PayPal</strong>
                <span>{dispute.allowedActions.map(readableStatus).join(" · ")}</span>
              </div>
            ) : null}
          </Modal.Section>
          <Modal.Section>
            <div className="store-support-heading"><h2>Assisted monitoring</h2><p>Configuration only; support Admin does not submit financial responses.</p></div>
            <dl className="store-support-definition-grid store-support-definition-grid--four">
              <DetailDefinition label="Configured" value={automation.configured === true ? "Yes" : automation.configured === false ? "No" : "Unknown"} />
              <DetailDefinition label="Monitoring" value={automation.enabled === true ? "Enabled" : automation.enabled === false ? "Disabled" : "Unknown"} />
              <DetailDefinition label="Merchant approval" value={automation.requireMerchantApproval === true ? "Required" : automation.requireMerchantApproval === false ? "Not required" : "Unknown"} />
              <DetailDefinition label="Dry run" value={automation.dryRun === true ? "On" : automation.dryRun === false ? "Off" : "Unknown"} />
            </dl>
          </Modal.Section>
          <Modal.Section>
            <div className="store-support-heading"><h2>Redacted case activity</h2><p>Operational history only. Raw requests, responses, buyer evidence, and credentials are excluded.</p></div>
            {activities.length ? (
              <ol className="store-support-case-timeline">
                {activities.map((activity, index) => (
                  <li key={activity?.id || index}>
                    <div>
                      <Badge status={statusTone(activity?.status)}>{readableStatus(activity?.status)}</Badge>
                      <strong>{readableStatus(activity?.actionType || activity?.source, "Case activity")}</strong>
                    </div>
                    <p>{activity?.errorMessage || activity?.errorCode || "No operational error recorded."}</p>
                    <span>{formatDateTime(activity?.completedAt || activity?.createdAt)}</span>
                  </li>
                ))}
              </ol>
            ) : <p className="store-support-empty">No redacted activity was returned.</p>}
          </Modal.Section>
        </>
      )}
    </Modal>
  );
};

const DisputesLayout = () => {
  const navigate = useNavigate();
  const workspace = useStoreWorkspace();
  const model = workspaceModel(workspace.data, workspace.shopId);
  const { disputes, recentDisputes, connections, paypalAccounts } = model;
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [view, setView] = useState("all");
  const [page, setPage] = useState(0);
  const [selectedDispute, setSelectedDispute] = useState({ shopId: null, id: null });
  const [refreshKey, setRefreshKey] = useState(0);
  const [caseState, setCaseState] = useState({
    shopId: String(workspace.shopId),
    loading: true,
    data: null,
    error: null,
  });

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => setPage(0), [debouncedQuery, view]);

  const loadCases = useCallback(() => {
    const controller = new AbortController();
    const shopId = String(workspace.shopId);
    setCaseState((current) =>
      current.shopId === shopId
        ? { ...current, loading: true, error: null }
        : { shopId, loading: true, data: null, error: null }
    );
    const search = {
      page,
      size: 25,
      query: debouncedQuery || undefined,
      state: view === "open" ? "OPEN" : view === "resolved" ? "RESOLVED" : undefined,
      attention: view === "attention" ? true : undefined,
      sort: view === "attention" ? "DUE_AT" : "UPDATED_AT",
      direction: view === "attention" ? "ASC" : "DESC",
    };
    getSupportStoreDisputes(shopId, search, { signal: controller.signal })
      .then((data) => {
        if (!controller.signal.aborted) {
          setCaseState({ shopId, loading: false, data, error: null, refreshKey });
        }
      })
      .catch((error) => {
        if (!controller.signal.aborted && error?.name !== "AbortError") {
          setCaseState((current) => ({
            ...(current.shopId === shopId
              ? current
              : { shopId, data: null }),
            loading: false,
            error: error?.message || "Disputes could not be refreshed.",
          }));
        }
      });
    return () => controller.abort();
  }, [debouncedQuery, page, refreshKey, view, workspace.shopId]);

  useEffect(() => loadCases(), [loadCases]);

  const fallbackCases = useMemo(
    () => recentDisputes.map(normalizeSupportDispute).filter((dispute) => dispute.id),
    [recentDisputes]
  );
  const currentCaseState =
    caseState.shopId === String(workspace.shopId)
      ? caseState
      : { shopId: String(workspace.shopId), loading: true, data: null, error: null };
  const cases = useMemo(() => {
    const pageCases = Array.isArray(currentCaseState.data?.content)
      ? currentCaseState.data.content
      : [];
    return (pageCases.length || currentCaseState.data ? pageCases : fallbackCases)
      .map(normalizeSupportDispute)
      .filter((dispute) => dispute.id);
  }, [currentCaseState.data, fallbackCases]);
  const summary = currentCaseState.data?.summary || disputes;
  const selectedDisputeId =
    selectedDispute.shopId === String(workspace.shopId) ? selectedDispute.id : null;

  const paypal = connections.paypal || {};
  const paypalConnected =
    paypal.connected === true ||
    ["CONNECTED", "RATE_LIMITED", "DISPUTE_SYNC_ERROR", "DISPUTE_SYNC_STALLED"].includes(
      String(paypal.connectionHealth || paypal.status || "").toUpperCase()
    ) ||
    Number(paypal.activeAccounts) > 0 ||
    paypalAccounts.some((account) => account?.usable === true);
  const attentionCount =
    summary.attention ??
    (cases.length ? cases.filter(requiresAttention).length : undefined);
  const openCount =
    summary.open ?? (cases.length ? cases.filter((item) => isOpen(item.status)).length : undefined);
  const totalCount = summary.total ?? currentCaseState.data?.totalElements ?? (cases.length ? cases.length : undefined);

  const rows = cases.map((dispute, index) => {
    const attention = requiresAttention(dispute);
    const orderSearchPath = disputeOrderSearchPath(workspace.shopId, dispute);
    return (
      <IndexTable.Row id={dispute.id} key={dispute.id} position={index}>
        <IndexTable.Cell>
          <strong>{dispute.id}</strong>
          {dispute.isSample ? <span className="store-support-cell-note">Sample case</span> : null}
          {dispute.transaction ? (
            <span className="store-support-cell-note">Transaction {dispute.transaction}</span>
          ) : null}
        </IndexTable.Cell>
        <IndexTable.Cell>
          {orderSearchPath ? (
            <Link to={orderSearchPath}>
              {dispute.order || "Find matching order"}
            </Link>
          ) : (
            "Not matched"
          )}
          {dispute.account ? (
            <span className="store-support-cell-note">PayPal {dispute.account}</span>
          ) : null}
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Badge status={statusTone(attention ? "ATTENTION" : dispute.status)}>
            {readableStatus(dispute.status)}
          </Badge>
          {dispute.stage ? (
            <span className="store-support-cell-note">{readableStatus(dispute.stage)}</span>
          ) : null}
        </IndexTable.Cell>
        <IndexTable.Cell>{readableStatus(dispute.reason, "Not recorded")}</IndexTable.Cell>
        <IndexTable.Cell>{formatMoney(dispute.amountValue, dispute.currency)}</IndexTable.Cell>
        <IndexTable.Cell>
          <span className={attention ? "store-support-deadline--attention" : ""}>
            {deadlineStatus(dispute)}
          </span>
          {dispute.updatedAt ? (
            <span className="store-support-cell-note">
              Updated {formatRelativeTime(dispute.updatedAt)}
            </span>
          ) : null}
        </IndexTable.Cell>
        <IndexTable.Cell>
          {dispute.recommendedAction ||
            (attention ? "Merchant response should be reviewed" : "No support action returned")}
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Button
            onClick={() =>
              setSelectedDispute({ shopId: String(workspace.shopId), id: dispute.id })
            }
          >
            View
          </Button>
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  return (
    <StorePageFrame
      title="Disputes"
      maxWidth="1500px"
      gap="16px"
      primaryAction={{
        content: "Refresh",
        onAction: () => {
          workspace.refresh();
          setRefreshKey((value) => value + 1);
        },
      }}
    >
      <WorkspaceState
        loading={workspace.loading}
        refreshing={workspace.refreshing}
        error={workspace.error}
        hasData={Boolean(workspace.data)}
        onRetry={() => workspace.refresh()}
      >
        {currentCaseState.error ? (
          <Banner
            title="The dispute list could not be refreshed"
            status="warning"
            action={{ content: "Retry", onAction: () => setRefreshKey((value) => value + 1) }}
          >
            <p>{currentCaseState.error}{fallbackCases.length ? " Showing recent cases from the last workspace snapshot." : ""}</p>
          </Banner>
        ) : null}
        {!paypalConnected ? (
          <Banner
            title="PayPal is not connected"
            status="critical"
            action={{
              content: "View connection details",
              onAction: () => navigate(`/store/${workspace.shopId}/configuration`),
            }}
          >
            <p>
              Live disputes cannot refresh until the merchant reconnects PayPal from PalSync in
              Shopify Admin. Support cannot enter merchant credentials here.
            </p>
          </Banner>
        ) : summary.syncError ? (
          <Banner title="PayPal disputes need attention" status="critical">
            <p>{summary.syncError}</p>
          </Banner>
        ) : null}

        <Card>
          <div className="store-support-hero store-support-hero--compact">
            <div>
              <div className="store-support-hero__title">
                <h2>PayPal dispute operations</h2>
                <Badge status={statusTone(summary.syncStatus || (paypalConnected ? "READY" : "DISCONNECTED"))}>
                  {readableStatus(summary.syncStatus || (paypalConnected ? "Ready" : "Disconnected"))}
                </Badge>
              </div>
              <p>Read-only support view of merchant cases, deadlines, and reconciliation health.</p>
              <span>
                Last PayPal refresh {formatRelativeTime(summary.lastSyncedAt)} · account data is
                limited to support-safe identifiers
              </span>
            </div>
          </div>
        </Card>

        <div className="store-support-dispute-metrics">
          <Metric label="Total cases" value={totalCount} hint="Returned by support data" />
          <Metric label="Open" value={openCount} hint="All non-resolved PayPal states" />
          <Metric
            label="Needs attention"
            value={attentionCount}
            hint="Seller action or deadline near"
            tone={attentionCount === null || attentionCount === undefined ? "neutral" : Number(attentionCount) > 0 ? "critical" : "success"}
          />
          <Metric label="Overdue" value={summary.overdue} hint="Response deadline passed" tone={Number(summary.overdue) > 0 ? "critical" : "neutral"} />
          <Metric label="Under review" value={summary.underReview} hint="PayPal is reviewing" />
        </div>

        <Card>
          <div className="store-support-disputes-toolbar">
            <TextField
              label="Search disputes"
              labelHidden
              value={query}
              onChange={setQuery}
              placeholder="Search dispute ID, transaction, invoice, or reason"
              clearButton
              onClearButtonClick={() => setQuery("")}
              autoComplete="off"
            />
            <Select
              label="Case view"
              labelHidden
              value={view}
              onChange={setView}
              options={[
                { label: "All cases", value: "all" },
                { label: "Needs attention", value: "attention" },
                { label: "PayPal state: Open", value: "open" },
                { label: "PayPal state: Resolved", value: "resolved" },
              ]}
            />
          </div>
          <IndexTable
            resourceName={{ singular: "dispute", plural: "disputes" }}
            itemCount={cases.length}
            selectable={false}
            headings={[
              { title: "Dispute" },
              { title: "Order / account" },
              { title: "Status" },
              { title: "Reason" },
              { title: "Amount" },
              { title: "Deadline / activity" },
              { title: "Next support step" },
              { title: "" },
            ]}
          >
            {rows}
          </IndexTable>
          {currentCaseState.loading && !currentCaseState.data ? (
            <div className="store-support-table-empty" role="status">Loading disputes…</div>
          ) : cases.length === 0 ? (
            <div className="store-support-table-empty" role="status">
              {query || view !== "all"
                ? "No disputes match this search and view."
                : "No dispute cases were returned for this store."}
            </div>
          ) : null}
          {Number(currentCaseState.data?.totalPages) > 1 ? (
            <div className="store-support-directory-pagination">
              <span>Page {page + 1} of {currentCaseState.data.totalPages}</span>
              <Pagination
                hasPrevious={page > 0}
                onPrevious={() => setPage((value) => Math.max(0, value - 1))}
                hasNext={page + 1 < Number(currentCaseState.data.totalPages)}
                onNext={() => setPage((value) => value + 1)}
              />
            </div>
          ) : null}
        </Card>
        <DisputeDetailModal
          shopId={workspace.shopId}
          disputeId={selectedDisputeId}
          onClose={() => setSelectedDispute({ shopId: null, id: null })}
        />
      </WorkspaceState>
    </StorePageFrame>
  );
};

export default DisputesLayout;
